/**
 * Das Kontextmenue einer Aufgabenzeile im Tasks-Tab (Auftrag "Tasks Sammelbearbeitung", 2026-09-04,
 * Anforderung Damian Gembries: "Die Umsetzung [Strg/Shift-Mehrfachauswahl + Rechtsklick-Loeschen auf
 * dem Bestellboard] funktioniert einwandfrei, bekommen wir das auch fuer die Untertasks hin? Top
 * waere wenn man untertasks auch mit strg/shift markieren und mit rechtsklick loeschen kann.").
 *
 * ================= Wie diese Komponente eingehaengt wird =================
 *
 * Baut nach demselben Grundmuster wie `Board/KontextMenue.tsx` (Positionierung nach dem Zeichnen,
 * Schliessen bei Escape/Klick ausserhalb/Fensterverlust, Fokus auf den ersten Knopf, "erst
 * schliessen, dann die Aktion melden") - reduziert auf die zwei Loesch-Eintraege, die dieser Reiter
 * braucht. `TasksTab.tsx` haelt `{ aufgabe, index, x, y } | null` als eigenen, LOKALEN Zustand
 * (anders als beim Board, das seinen Kontextmenue-Zustand in `App.tsx` haelt - die Aufgabenliste
 * braucht keinen Zustand ausserhalb dieses Reiters) und setzt ihn in ihrem eigenen `onContextMenu`-
 * Handler (`kontextmenueOeffnen`, dort `event.preventDefault()` - wie beim Board NICHT in dieser
 * Komponente, `PhasenListe.tsx#AufgabenZeile` reicht das rohe `MouseEvent` unveraendert weiter).
 *
 * Portalt wie `AufgabenDetail.tsx` nach `#drawer` (GESCHWISTER von `.drawer-body`, nicht dessen
 * Kind): der Rechte-Durchgang des Rahmens (`Drawer.tsx`) scannt nur `.drawer-body input, select,
 * textarea, button` NACH JEDEM ZEICHNEN VON `Drawer.tsx` SELBST - ein Menue, das erst beim
 * Rechtsklick MONTIERT wird (anders als die von Anfang an vorhandenen Bedienelemente wie
 * `.task-row-check`), wuerde vom naechsten Durchlauf des Durchgangs nur zufaellig noch VOR dem
 * naechsten Rechtsklick erfasst - derselbe Grund, aus dem `AufgabenDetail.tsx` ihren Loeschknopf
 * schon immer ausserhalb von `.drawer-body` haelt (dortiger Kopfkommentar, "BEFUND: das Panel
 * unterliegt dem Rechte-Durchgang NIE"). Diese Komponente uebernimmt bewusst DIESELBE Haltung, statt
 * eine neue, zeitlich fragile Abhaengigkeit vom Durchgang einzugehen.
 *
 * Nutzt bewusst DIESELBEN CSS-Klassen wie das Board-Kontextmenue (`.order-context-menu` samt Kindern,
 * `bestand.css:3154-3208`) statt eigener - der visuelle Rahmen (Karte, Schatten, `position: fixed`,
 * Knopfzeilen) ist generisch, keine boardspezifische Regel; eine zweite, praktisch identische
 * Regelgruppe waere unnoetige Verdopplung.
 *
 * ================= Die beiden Eintraege (Akzeptanzkriterium 5) =================
 *
 * - `istInAuswahl && ausgewaehlteAnzahl >= 2`: EIN Eintrag "Löschen ({N} Aufgaben)" (Aktion
 *   `"bulkDelete"`) - der Aufrufer loescht dafuer die GESAMTE aktuelle Mehrfachauswahl, nicht nur die
 *   rechtsgeklickte Zeile (dasselbe Muster wie Board's `"bulkStatus"`, das die mitgegebene `id`
 *   ebenfalls ignoriert - siehe dortiger Kopfkommentar, "ZWEITE Erweiterung").
 * - Sonst (nichts markiert, oder die rechtsgeklickte Zeile ist nicht Teil einer Auswahl mit
 *   mindestens zwei Elementen): EIN Eintrag "Aufgabe löschen" (Aktion `"delete"`) - trifft NUR die
 *   rechtsgeklickte Zeile. `TasksTab.tsx#kontextmenueOeffnen` setzt die Auswahl in diesem Fall VORAB
 *   auf genau diese eine Zeile (siehe dortiger Kommentar) - eine bewusste ERWEITERUNG gegenueber dem
 *   woertlichen Board-Vorbild, das die Auswahl bei einem Rechtsklick nie veraendert (dort trifft
 *   "Löschen" ohnehin IMMER nur die rechtsgeklickte Zeile, nie die Auswahl - ein Zuruecksetzen waere
 *   dort wirkungslose Mehrarbeit).
 *
 * Beide Eintraege schliessen einander AUS (nie gleichzeitig sichtbar) - anders als beim Board, wo der
 * einzelne "Löschen"-Knopf und der Sammel-Knopf "Status ändern für N…" NEBENEINANDER stehen koennen
 * (dort zwei UNABHAENGIGE Aktionen; hier zwei VARIANTEN derselben Aktion "loeschen").
 *
 * ================= Annahme =================
 *
 * Wie `Board/KontextMenue.tsx` (dortige Annahme 1) rendert diese Komponente NICHTS, wenn `aufgabe`
 * fehlt (`return null`) - kein Baseline-Vergleich deckt einen offenen Zustand, den es im Original
 * (`index.html`s `#taskContextMenu`) fuer Untertasks nie gab.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { OrderTask } from "../../../lib/types.ts";
import { Ikone } from "../../Ikone.tsx";

export type AufgabenKontextAktion = "delete" | "bulkDelete";

export interface AufgabenKontextMenueProps {
  /** `null`/`undefined` -> geschlossen, siehe Annahme im Kopfkommentar. */
  aufgabe: OrderTask | null | undefined;
  /** `event.clientX`/`event.clientY` beim Oeffnen. */
  x: number;
  y: number;
  /** Groesse der aktuellen Mehrfachauswahl (`TasksTab.tsx`s `ausgewaehlt.size`). */
  ausgewaehlteAnzahl?: number;
  /** Ist `aufgabe.id` Teil der aktuellen Mehrfachauswahl? */
  istInAuswahl?: boolean;
  onSchliessen?: () => void;
  onAktion?: (aktion: AufgabenKontextAktion) => void;
}

export function AufgabenKontextMenue({
  aufgabe,
  x,
  y,
  ausgewaehlteAnzahl = 0,
  istInAuswahl = false,
  onSchliessen,
  onAktion,
}: AufgabenKontextMenueProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const offen = aufgabe != null;

  // Siehe `AufgabenDetail.tsx`, Kopfkommentar "Warum createPortal": `#drawer` steht erst NACH dem
  // ersten Commit im DOM - `useLayoutEffect` statt `useState`-Initialwert, damit der Sucher sicher
  // NACH dem gemeinsamen ersten Zeichnen laeuft.
  const [ziel, setZiel] = useState<Element | null>(null);
  useLayoutEffect(() => {
    setZiel(document.getElementById("drawer"));
  }, []);

  // Eigenheit (wie Board): NACH dem Zeichnen messen und positionieren, dann den ersten Knopf
  // fokussieren.
  //
  // `ziel` MUSS in der Abhaengigkeitsliste stehen, obwohl der Effekt selbst nicht direkt darauf
  // zugreift: das Portal-Ziel steht (siehe Effekt oben) erst nach einem ZWEITEN Zeichnen fest - beim
  // ERSTEN Durchlauf dieses Effekts (`ziel` noch `null`) ist `menuRef.current` mangels Portal-Inhalt
  // noch `null`, der fruehe Rueckgabewert oben greift. Ohne `ziel` hier haette React beim zweiten
  // Zeichnen (in dem der Knoten TATSAECHLICH steht) keinen Grund, diesen Effekt erneut laufen zu
  // lassen - `offen`/`aufgabe`/`x`/`y` aendern sich zwischen beiden Durchlaeufen ja nicht. GEMESSEN
  // (nicht vermutet): ohne diese Zeile blieben Position UND Fokus in JEDEM Testfall leer/undefiniert.
  useLayoutEffect(() => {
    if (!offen || !menuRef.current) return;
    const knoten = menuRef.current;
    const rect = knoten.getBoundingClientRect();
    knoten.style.left = `${Math.max(8, Math.min(x, window.innerWidth - rect.width - 8))}px`;
    knoten.style.top = `${Math.max(8, Math.min(y, window.innerHeight - rect.height - 8))}px`;
    knoten.querySelector("button")?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offen, aufgabe, x, y, ziel]);

  // Schliesst bei Escape, Klick ausserhalb, Fensterverlust - wie Board's Kontextmenue.
  useEffect(() => {
    if (!offen) return;
    const beiTaste = (ereignis: KeyboardEvent): void => {
      if (ereignis.key === "Escape") onSchliessen?.();
    };
    const beiKlick = (ereignis: MouseEvent): void => {
      if (!menuRef.current?.contains(ereignis.target as Node)) onSchliessen?.();
    };
    const beiUnschaerfe = (): void => onSchliessen?.();
    document.addEventListener("keydown", beiTaste);
    document.addEventListener("click", beiKlick);
    window.addEventListener("blur", beiUnschaerfe);
    return () => {
      document.removeEventListener("keydown", beiTaste);
      document.removeEventListener("click", beiKlick);
      window.removeEventListener("blur", beiUnschaerfe);
    };
  }, [offen, onSchliessen]);

  if (!aufgabe || !ziel) return null;

  const ausloesen = (aktion: AufgabenKontextAktion): void => {
    // Wie Board: erst schliessen, dann die Aktion melden.
    onSchliessen?.();
    onAktion?.(aktion);
  };

  const sammelfall = istInAuswahl && ausgewaehlteAnzahl >= 2;

  return createPortal(
    <div ref={menuRef} className="order-context-menu open" role="menu" aria-hidden="false">
      {sammelfall ? (
        <button className="danger" type="button" role="menuitem" onClick={() => ausloesen("bulkDelete")}>
          <span className="icon-label">
            <Ikone name="trash-2" />
            {`Löschen (${ausgewaehlteAnzahl} Aufgaben)`}
          </span>
        </button>
      ) : (
        <button className="danger" type="button" role="menuitem" onClick={() => ausloesen("delete")}>
          <span className="icon-label">
            <Ikone name="trash-2" />
            Aufgabe löschen
          </span>
        </button>
      )}
    </div>,
    ziel,
  );
}
